"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  RichTextInput,
  type RichTextInputHandle,
} from "@/components/RichTextInput";
import { AttachmentTray, AddAttachmentButton } from "@/components/AttachmentTray";
import { useAttachments } from "@/lib/hooks/useAttachments";
import { useDraft } from "@/lib/hooks/useDraft";
import { SubmitHint, SubmitTooltip } from "@/components/SubmitShortcut";
import { matchAddressedBot, type AddressableBot } from "@/lib/botAddress";
import type { UploadScope } from "@theta/api-client";
import type { CreateMessageInput } from "@/lib/api/messages";

/**
 * The DM composer. A close cousin of CommentComposer with three deliberate
 * differences: no `groupId` (a private message has no category, so only the
 * standard emoji set autocompletes), no autofocus (it is permanently mounted at
 * the bottom of a thread), and it delegates the actual send to its parent.
 *
 * IT NO LONGER BUILDS A BODY. `buildPostBody` glued `![name](upload://…)`
 * tokens onto the end of the text because the API had no other way to carry a
 * file; v1 takes `attachments: [{uploadId, kind}]` as rows, in display order
 * (§1.12), so the composer hands the two apart and nothing has to parse them
 * back out on the way in.
 */

/** What the composer produces: the text, and the uploads to hang off it. */
export interface ComposedMessage {
  text: string;
  attachments: MessageAttachmentInput[];
}

type MessageAttachmentInput = NonNullable<CreateMessageInput["attachments"]>[number];

/**
 * An upload is authorised at the moment it is uploaded (§1.11), so a composer
 * with no conversation yet has no scope to declare — and `POST /conversations`
 * takes no attachments, so it can never need one. `useAttachments` must still
 * be called unconditionally (rules of hooks); this scope is never reached,
 * because the attach button and the paste handler are both gated on
 * `uploadScope`.
 */
const NO_SCOPE: UploadScope = { type: "conversation", id: 0 };

/** How long the sparkle lives — the animation in globals.css is this long. */
const SPARKLE_MS = 1000;
export function MessageComposer({
  draftScope,
  uploadScope,
  onSend,
  placeholder = "Skriv en besked…",
  submitLabel = "Send",
  disabled = false,
  bots,
}: {
  /** useDraft scope, e.g. `message:1234` or `message:new`. */
  draftScope: string;
  /**
   * Where an uploaded file belongs, for the ACL it is given before anything
   * references it. Omitted where there is no conversation yet, which is also
   * where attachments are not offered at all.
   */
  uploadScope?: UploadScope;
  /** Sends the text and its uploads. Rejecting shows a Danish error. */
  onSend: (composed: ComposedMessage) => Promise<unknown>;
  placeholder?: string;
  submitLabel?: string;
  disabled?: boolean;
  /**
   * The bots seated in a GROUP conversation, which only answer a message
   * that opens "Kære <navn>" (lib/botAddress.ts). The moment the draft does,
   * those words light up in the text and a sparkle pops beside them once —
   * so the writer knows they hit the words exactly, before sending. Omitted
   * in a 1:1 with a bot, where every message is answered and there is
   * nothing to point out.
   */
  bots?: readonly AddressableBot[];
}) {
  const mentionRef = useRef<RichTextInputHandle>(null);
  const draft = useDraft(draftScope);
  const body = draft.value;
  const setBody = draft.setValue;

  const addressed = useMemo(
    () => (bots && bots.length > 0 ? matchAddressedBot(body, bots) : null),
    [body, bots],
  );
  /**
   * ONE sparkle per greeting, on the keystroke that completes it — not on
   * every keystroke while it still matches, and again only after the greeting
   * was broken and re-made. `sparkle` is a counter rather than a boolean so a
   * re-made greeting remounts the element and replays the animation.
   */
  const [sparkle, setSparkle] = useState(0);
  const wasAddressed = useRef(false);
  useEffect(() => {
    const now = addressed !== null;
    if (now && !wasAddressed.current) setSparkle((n) => n + 1);
    wasAddressed.current = now;
  }, [addressed]);
  // Gone when its animation is over — a timer rather than `animationend`,
  // which reduced-motion settings and some webviews never fire.
  useEffect(() => {
    if (sparkle === 0) return;
    const timer = setTimeout(() => setSparkle(0), SPARKLE_MS);
    return () => clearTimeout(timer);
  }, [sparkle]);

  const files = useAttachments(uploadScope ?? NO_SCOPE);
  const canAttach = uploadScope != null;
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A message carrying only a photo is a perfectly good message.
  const canSubmit =
    (body.trim().length > 0 || files.hasContent) &&
    !files.isUploading &&
    !sending &&
    !disabled;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const text = (mentionRef.current?.getRaw() ?? body).trim();
    // `uploadIds` is the same list every other composer sends — ids in tray
    // order, and no `kind`: the tray has three (`image | video | file`) where
    // the server has four, and the server reads the upload row instead of a
    // claim about it (OSO-437). This built its own copy, with the tray's
    // `kind` in it, which is how a voice memo was sent as a plain file.
    const attachments: MessageAttachmentInput[] = files.uploadIds;
    setSending(true);
    setError(null);
    try {
      await onSend({ text, attachments });
      setBody("");
      draft.clear();
      mentionRef.current?.clearMentions();
      files.reset();
    } catch (e) {
      // One error shape now (`ThetaError extends Error`), so the server's own
      // Danish message comes through wherever it sent one.
      setError(
        e instanceof Error && e.message
          ? e.message
          : "Beskeden kunne ikke sendes. Prøv igen."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      className="group"
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <div className="relative">
        {sparkle > 0 && (
          <span
            key={sparkle}
            data-testid="bot-address-sparkle"
            aria-hidden="true"
            className="theta-bot-address-sparkle"
          >
            ✨
          </span>
        )}
        <RichTextInput
          ref={mentionRef}
          ariaLabel="Skriv en besked"
          value={body}
          onChange={setBody}
          onSubmit={handleSubmit}
          onPasteFiles={canAttach ? files.addFiles : undefined}
          placeholder={placeholder}
          disabled={sending || disabled}
          multiline
          hideFormattingIcon
          highlightLeading={addressed?.length ?? null}
          /* `text-sm` ON PURPOSE, not `text-content` (OSO-629): a composer
             matches what its destination renders, and a sent message's bubble
             is drawn at `text-sm` (MessageBubble.tsx) — the DM surface is
             deliberately denser than post/comment bodies. Exempted, with this
             reason, in contentTextSize.test.tsx. */
          className="w-full bg-muted rounded-[18px] pl-4 pr-20 py-2 text-sm text-foreground placeholder:text-subtle-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-surface transition-colors"
        />
        {/* A message may carry any file, not just media, so the shared
            button's image/video-only default is widened here. */}
        {canAttach && (
        <AddAttachmentButton
          onFiles={files.addFiles}
          accept="*/*"
          label="Vedhæft fil"
          className="absolute right-11 bottom-1 w-7 h-7 rounded-full flex items-center justify-center text-subtle-foreground hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
        </AddAttachmentButton>
        )}
        <SubmitTooltip label="Send besked" className="absolute right-2 bottom-1">
          <button
            type="submit"
            disabled={!canSubmit}
            aria-label={submitLabel}
            className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
              !canSubmit
                ? "text-subtle-foreground cursor-not-allowed"
                : "bg-indigo-600 hover:bg-indigo-700 text-white"
            }`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>
        </SubmitTooltip>
      </div>
      <SubmitHint value={body} />

      {error && (
        <p className="text-xs text-destructive mt-1 pl-4">{error}</p>
      )}

      {files.error && (
        <p className="text-xs text-destructive mt-1 pl-4">
          {files.error}
        </p>
      )}

      {files.attachments.length > 0 && (
        <AttachmentTray
          attachments={files.attachments}
          onRemove={files.remove}
          onMove={files.move}
          size="sm"
        />
      )}
    </form>
  );
}
